import React from "react";
import { Divider, Grid } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import { ScheduleFrequencies } from "constants/reportSubscriptions";
import {
	SubscriptionNameField,
	ReportPickerField,
	CurrentFiltersPanel,
	FrequencyRadioGroup,
	DaysOfWeekToggle,
	DeliveryTimeField,
	TimezoneField,
	DeliveryEmailField,
	SubscriptionSummary,
} from "./fields";

const useStyles = makeStyles()(() => ({
	stack: {
		display: "flex",
		flexDirection: "column",
		gap: "20px",
	},
	scheduleHeader: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "13px",
		textTransform: "uppercase",
		color: "#6b7280",
		letterSpacing: "0.05em",
	},
	scheduleDivider: {
		display: "flex",
		alignItems: "center",
		gap: "10px",
	},
	dividerLine: {
		flex: 1,
	},
}));

// Pure composition: receives the form state from useScheduleForm via props,
// fans state out to atomic field components. No data fetching, no business
// logic — keeps the modal a thin shell over the form, and the form a thin
// shell over its fields.
const ScheduleReportForm = ({
	values,
	setField,
	setFrequency,
	toggleDay,
	summary,
	availableReports,
	currentFilters,
	fallbackEmail,
	lockReportSelection = false,
}) => {
	const { classes } = useStyles();
	const { t } = useTranslation();

	return (
		<div className={classes.stack}>
			<ReportPickerField
				reports={availableReports}
				value={values.reportType}
				onChange={(v) => setField("reportType", v)}
				disabled={lockReportSelection}
			/>

			<SubscriptionNameField
				value={values.name}
				onChange={(v) => setField("name", v)}
			/>

			<CurrentFiltersPanel filters={currentFilters} />

			<div className={classes.scheduleDivider}>
				<Divider className={classes.dividerLine} />
				<span className={classes.scheduleHeader}>{t("label.schedule")}</span>
				<Divider className={classes.dividerLine} />
			</div>

			<FrequencyRadioGroup
				value={values.frequency}
				onChange={setFrequency}
			/>

			{values.frequency === ScheduleFrequencies.Weekly ? (
				<DaysOfWeekToggle
					selectedDays={values.daysOfWeek}
					onToggle={toggleDay}
				/>
			) : null}

			<Grid container spacing={2}>
				<Grid item xs={12} sm={4}>
					<DeliveryTimeField
						value={values.timeOfDay}
						onChange={(v) => setField("timeOfDay", v)}
					/>
				</Grid>
				<Grid item xs={12} sm={8}>
					<TimezoneField
						value={values.timezone}
						onChange={(v) => setField("timezone", v)}
					/>
				</Grid>
			</Grid>

			<DeliveryEmailField
				value={values.deliveryEmail}
				onChange={(v) => setField("deliveryEmail", v)}
				fallbackEmail={fallbackEmail}
			/>

			<SubscriptionSummary summary={summary} />
		</div>
	);
};

export default ScheduleReportForm;
